/* src/ui/windows/buildingInspector.ts — what is this building, and can I have it? (TASKS.md 4.2)
 *
 * Click a footprint in the city and this opens on it: what OSM says the building is, what it
 * costs, who owns it, what stands on it, and the one or two things you can do about that.
 *
 * Every figure is the server's. The client does not price land, work out a construction cost, or
 * decide whether a build option is available — it asks for a parcel and renders the answer
 * (CLAUDE.md non-negotiable #2). `buildOptions` arrives empty on a building that is not yours,
 * which is why there is no client-side check for that anywhere below: an option you cannot take
 * never reaches the UI.
 *
 * One window, reused. Clicking a second building re-points the same panel rather than opening a
 * pile of them — a city of 16,000 buildings would otherwise be a very easy way to bury the
 * screen.
 */

import { emit } from '../../core/events.ts';
import { gameStore } from '../../core/store.ts';
import type { GameState, Parcel } from '../../core/types.ts';
import { buyParcel, fetchParcel, sellParcel } from '../../net/gameApi.ts';
import { incomeInterpolator } from '../../sim/incomeInterpolator.ts';
import { confirmDialog } from '../confirm.ts';
import { el, text } from '../dom.ts';
import { formatMoney } from '../format.ts';
import { toast } from '../toast.ts';
import type { WindowManager } from './windowChrome.ts';

const WINDOW_ID = 'building';

/** OSM's tag vocabulary is not a sentence. Turn `apartments` into something readable. */
function landUseLabel(raw: string | null): string {
  if (raw === null || raw === '' || raw === 'yes') return 'Building';
  return raw.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Street frontage, in the words a person would use. */
function frontageLabel(raw: string | null): string {
  switch (raw) {
    case 'primary':
    case 'primary_link':
      return 'Main road';
    case 'secondary':
    case 'secondary_link':
      return 'Arterial road';
    case 'tertiary':
    case 'tertiary_link':
      return 'Through road';
    case 'residential':
    case 'unclassified':
      return 'Residential street';
    case 'living_street':
      return 'Living street';
    case 'pedestrian':
      return 'Pedestrian street';
    case 'footway':
    case 'path':
      return 'Footpath';
    case 'service':
      return 'Service lane';
    case 'motorway':
    case 'trunk':
      return 'Motorway';
    default:
      return raw === null ? 'No frontage' : raw.replace(/_/g, ' ');
  }
}

/** A one-line reading of what is installed, for the owner's card. */
function fixtureSummary(p: Parcel): string {
  const count = (kind: string): number => p.fixtures.filter((f) => f.kind === kind).length;
  const parts: string[] = [];
  const stairs = count('stairs');
  const shelves = count('shelf');
  const checkouts = count('checkout');
  const racks = count('storage');
  if (stairs > 0) parts.push(`${String(stairs)} stair${stairs === 1 ? '' : 's'}`);
  if (shelves > 0) parts.push(`${String(shelves)} shel${shelves === 1 ? 'f' : 'ves'}`);
  if (checkouts > 0) parts.push(`${String(checkouts)} checkout${checkouts === 1 ? '' : 's'}`);
  if (racks > 0) parts.push(`${String(racks)} rack${racks === 1 ? '' : 's'}`);
  return parts.length > 0 ? parts.join(' · ') : 'Empty — walk in and fit it out';
}

export interface BuildingInspector {
  /** Point the panel at a parcel and bring it forward. */
  show: (parcel: Parcel) => void;
  close: () => void;
}

/**
 * Open (or re-point) the inspector. `root` is where modal dialogs mount — the confirmation for a
 * purchase belongs to the page, not inside a draggable window that could be dragged away from it.
 */
export function openBuildingInspector(
  windows: WindowManager,
  root: HTMLElement,
  parcel: Parcel,
): BuildingInspector {
  const existing = windows.get(WINDOW_ID);
  if (existing) {
    const api = existing.userData as BuildingInspector | undefined;
    if (api) {
      api.show(parcel);
      existing.focus();
      return api;
    }
  }

  let current: Parcel = parcel;
  let busy = false;

  const win = windows.open({ id: WINDOW_ID, title: 'Building', width: 350 });

  // ── header ──────────────────────────────────────────────────────────────────────────
  const kindLine = text('');
  const nameLine = text('');
  const badge = el('span', { class: 'gf-bi-badge' });

  // ── stats ───────────────────────────────────────────────────────────────────────────
  const statGrid = el('div', { class: 'gf-bi-stats' });

  // ── price / ownership ───────────────────────────────────────────────────────────────
  const priceLabel = text('ASKING PRICE');
  const priceValue = text('');
  const priceNote = el('p', { class: 'gf-bi-note' });

  // ── actions ─────────────────────────────────────────────────────────────────────────
  const actions = el('div', { class: 'gf-bi-actions' });
  const buildList = el('div', { class: 'gf-bi-builds' });
  const status = el('p', { class: 'gf-bi-status' });

  win.body.replaceChildren(
    el(
      'div',
      { class: 'gf-bi-head' },
      el('div', null, el('div', { class: 'gf-bi-kind' }, kindLine), el('h3', { class: 'gf-bi-name' }, nameLine)),
      badge,
    ),
    el(
      'section',
      { class: 'gf-bi-card' },
      el('div', { class: 'gf-bi-pricerow' },
        el('span', { class: 'gf-bi-pricelabel' }, priceLabel),
        el('span', { class: 'gf-bi-price' }, priceValue)),
      priceNote,
    ),
    el('section', { class: 'gf-bi-card' }, statGrid),
    buildList,
    actions,
    status,
  );

  const apply = (state: GameState): void => {
    incomeInterpolator.sync(state);
    gameStore.set(state);
  };

  const fail = (err: unknown, fallback: string): void => {
    const msg = err instanceof Error ? err.message : fallback;
    status.textContent = msg;
    toast(msg);
  };

  /** Re-read the parcel after a mutation, so ownership and build options come from the server. */
  const refresh = async (): Promise<void> => {
    const fresh = await fetchParcel(current.id);
    if (fresh) {
      current = fresh;
      paint();
    }
  };

  const run = async (
    label: string,
    action: () => Promise<GameState>,
    done: string,
  ): Promise<void> => {
    if (busy) return;
    busy = true;
    status.textContent = label;
    try {
      apply(await action());
      await refresh();
      status.textContent = done;
    } catch (err) {
      fail(err, 'That did not work.');
    } finally {
      busy = false;
      paint();
    }
  };

  const stat = (label: string, value: string): HTMLElement =>
    el('div', { class: 'gf-bi-stat' },
      el('span', { class: 'gf-bi-statlabel' }, label),
      el('span', { class: 'gf-bi-statvalue' }, value));

  function paint(): void {
    const p = current;

    kindLine.textContent = `${landUseLabel(p.landUse).toUpperCase()} · MY CITY`;
    nameLine.textContent = p.osmWayId === null ? 'Building' : `Building #${String(p.osmWayId)}`;

    badge.textContent = p.ownedByMe ? 'YOURS' : p.owned ? 'OWNED' : 'FOR SALE';
    badge.className = `gf-bi-badge ${p.ownedByMe ? 'is-mine' : p.owned ? 'is-taken' : 'is-forsale'}`;

    // The price line says different things depending on whose building it is.
    if (p.ownedByMe) {
      priceLabel.textContent = 'YOU PAID';
      priceValue.textContent = formatMoney(p.pricePaid ?? p.price);
      priceNote.textContent = p.shop?.open
        ? `Trading — ${formatMoney(p.shop.revenuePerMin)}/min from ${p.shop.customersPerMin.toFixed(1)} customers/min`
        : fixtureSummary(p);
    } else if (p.owned) {
      priceLabel.textContent = 'VALUED AT';
      priceValue.textContent = formatMoney(p.price);
      priceNote.textContent = 'Another operator owns this one.';
    } else {
      priceLabel.textContent = 'ASKING PRICE';
      priceValue.textContent = formatMoney(p.price);
      const cash = gameStore.get()?.cash ?? 0;
      priceNote.textContent =
        cash >= p.price
          ? 'You can afford this.'
          : `You are ${formatMoney(p.price - cash)} short.`;
    }

    statGrid.replaceChildren(
      stat('Footprint', `${p.areaM2.toLocaleString()} m²`),
      stat('Floors', String(p.floors)),
      stat('Frontage', frontageLabel(p.roadClass)),
      stat('Land use', landUseLabel(p.landUse)),
    );

    // ── the owner's card: what is inside, what is coming, what it takes ───────────────
    if (p.ownedByMe && (p.fixtures.length > 0 || p.deliveries.length > 0)) {
      const lines: HTMLElement[] = [el('h4', { class: 'gf-bi-subhead' }, 'INSIDE')];
      lines.push(el('p', { class: 'gf-bi-note' }, fixtureSummary(p)));
      if (p.storage && p.storage.capacity > 0) {
        lines.push(
          el('p', { class: 'gf-bi-note' },
            `Storage ${String(Math.round(p.storage.used))} / ${String(Math.round(p.storage.capacity))}`),
        );
      }
      for (const d of p.deliveries) {
        const mins = Math.max(0, (new Date(d.arrivesAt).getTime() - Date.now()) / 60000);
        lines.push(
          el('p', { class: 'gf-bi-note' },
            `🚚 ${String(Math.round(d.qty))} × ${d.itemId} — ${mins < 1 ? 'arriving' : `~${String(Math.ceil(mins))} min`}`),
        );
      }
      buildList.replaceChildren(...lines);
      buildList.hidden = false;
    } else {
      buildList.replaceChildren();
      buildList.hidden = true;
    }

    // ── actions ───────────────────────────────────────────────────────────────────────
    const buttons: HTMLElement[] = [];

    /*
     * Your building: the thing to do is walk in. The descriptor carries the footprint, the
     * real floor count, how far the stairs reach and everything installed, so the render side
     * never reaches back into the UI (PLAN.md Part 2 §1). An empty building is still worth
     * entering — that is where the fitting out happens.
     */
    if (p.ownedByMe) {
      const name = p.osmWayId === null ? 'Building' : `Building #${String(p.osmWayId)}`;
      buttons.push(
        el(
          'button',
          {
            class: 'gf-bi-act is-primary',
            attrs: { type: 'button' },
            on: {
              click: () => {
                emit('world:enterinterior', {
                  parcelId: p.id,
                  name,
                  floors: p.floors,
                  reachableFloors: p.reachableFloors,
                  ring: p.ring,
                  lon: p.lon,
                  lat: p.lat,
                  osmWayId: p.osmWayId,
                  frontageDeg: p.frontageDeg,
                  fixtures: p.fixtures,
                  customersPerMin: p.shop?.customersPerMin ?? 0,
                });
              },
            },
          },
          `ENTER · ${String(p.floors)} ${p.floors === 1 ? 'FLOOR' : 'FLOORS'}`,
        ),
      );
    }

    if (!p.owned) {
      const cash = gameStore.get()?.cash ?? 0;
      const afford = cash >= p.price;
      buttons.push(
        el(
          'button',
          {
            class: `gf-bi-act is-primary${afford ? '' : ' is-disabled'}`,
            attrs: { type: 'button' },
            on: {
              click: () => {
                void (async () => {
                  if (!afford) {
                    status.textContent = `You are ${formatMoney(p.price - cash)} short.`;
                    return;
                  }
                  const ok = await confirmDialog(root, {
                    title: 'Buy this building?',
                    body:
                      `${landUseLabel(p.landUse)} of ${p.areaM2.toLocaleString()} m² on a ` +
                      `${frontageLabel(p.roadClass).toLowerCase()}, for ${formatMoney(p.price)}. ` +
                      `Selling it back returns 80%.`,
                    confirmLabel: `Buy · ${formatMoney(p.price)}`,
                  });
                  if (!ok) return;
                  await run('Buying…', () => buyParcel(p.id), 'Bought. It is yours.');
                })();
              },
            },
          },
          `BUY · ${formatMoney(p.price)}`,
        ),
      );
    }

    if (p.ownedByMe) {
      buttons.push(
        el(
          'button',
          {
            class: 'gf-bi-act',
            attrs: { type: 'button' },
            on: {
              click: () => {
                void (async () => {
                  // The resale figure is the server's (parcel_detail.resaleValue), never a
                  // client-side 0.8× — the haircut lives in one place, sell_parcel (CLAUDE.md #2).
                  const back = p.resaleValue;
                  const ok = await confirmDialog(root, {
                    title: 'Sell this building?',
                    body:
                      (back !== null
                        ? `You get ${formatMoney(back)} back of the ${formatMoney(p.pricePaid ?? p.price)} you paid.`
                        : `Selling returns less than you paid.`) +
                      (p.fixtures.length > 0
                        ? ' The fixtures come out and go home with you as items.'
                        : ''),
                    confirmLabel: back !== null ? `Sell · ${formatMoney(back)}` : 'Sell',
                    destructive: true,
                  });
                  if (!ok) return;
                  await run('Selling…', () => sellParcel(p.id), 'Sold.');
                })();
              },
            },
          },
          'SELL',
        ),
      );
    }

    actions.replaceChildren(...buttons);
    for (const b of buttons) {
      if (b instanceof HTMLButtonElement) b.disabled = busy;
    }
  }

  const api: BuildingInspector = {
    show(next: Parcel): void {
      current = next;
      status.textContent = '';
      // Point the world's gold outline at whichever building is now selected.
      emit('world:highlight', { ring: next.ring });
      paint();
    },
    close(): void {
      win.close();
    },
  };
  win.userData = api;

  // Cash changes while the panel is open — an affordable building becomes unaffordable when the
  // player restocks a cart, and the BUY button has to say so.
  const unsubscribe = gameStore.subscribe(() => {
    paint();
  });
  const close = win.close.bind(win);
  win.close = (): void => {
    unsubscribe();
    // The outline belongs to the open inspector; clear it when the panel goes.
    emit('world:highlight', { ring: [] });
    close();
  };

  // Outline the building this panel opened on, then draw it.
  emit('world:highlight', { ring: current.ring });
  paint();
  return api;
}
